// ============================================================
// xFreeScan - Register Page with OTP verification
// ============================================================
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { authApi } from '../../lib/api';
import { validateCorporateEmail } from '../../lib/emailValidation';
import { Shield, Mail, Building, Phone, ArrowRight, AlertCircle, User, Globe, KeyRound, ArrowLeft, RefreshCw, Info } from 'lucide-react';

const PAISES = [
  // --- America del Norte ---
  { code: 'CA', name: 'Canada', prefix: '+1' },
  { code: 'US', name: 'Estados Unidos', prefix: '+1' },
  { code: 'MX', name: 'Mexico', prefix: '+52' },
  // --- Centroamerica ---
  { code: 'BZ', name: 'Belice', prefix: '+501' },
  { code: 'GT', name: 'Guatemala', prefix: '+502' },
  { code: 'SV', name: 'El Salvador', prefix: '+503' },
  { code: 'HN', name: 'Honduras', prefix: '+504' },
  { code: 'NI', name: 'Nicaragua', prefix: '+505' },
  { code: 'CR', name: 'Costa Rica', prefix: '+506' },
  { code: 'PA', name: 'Panama', prefix: '+507' },
  // --- Caribe ---
  { code: 'CU', name: 'Cuba', prefix: '+53' },
  { code: 'DO', name: 'Republica Dominicana', prefix: '+1-809' },
  { code: 'HT', name: 'Haiti', prefix: '+509' },
  { code: 'JM', name: 'Jamaica', prefix: '+1-876' },
  { code: 'PR', name: 'Puerto Rico', prefix: '+1-787' },
  { code: 'TT', name: 'Trinidad y Tobago', prefix: '+1-868' },
  { code: 'BB', name: 'Barbados', prefix: '+1-246' },
  { code: 'BS', name: 'Bahamas', prefix: '+1-242' },
  { code: 'AG', name: 'Antigua y Barbuda', prefix: '+1-268' },
  { code: 'DM', name: 'Dominica', prefix: '+1-767' },
  { code: 'GD', name: 'Granada', prefix: '+1-473' },
  { code: 'KN', name: 'San Cristobal y Nieves', prefix: '+1-869' },
  { code: 'LC', name: 'Santa Lucia', prefix: '+1-758' },
  { code: 'VC', name: 'San Vicente y las Granadinas', prefix: '+1-784' },
  // --- America del Sur ---
  { code: 'AR', name: 'Argentina', prefix: '+54' },
  { code: 'BO', name: 'Bolivia', prefix: '+591' },
  { code: 'BR', name: 'Brasil', prefix: '+55' },
  { code: 'CL', name: 'Chile', prefix: '+56' },
  { code: 'CO', name: 'Colombia', prefix: '+57' },
  { code: 'EC', name: 'Ecuador', prefix: '+593' },
  { code: 'GY', name: 'Guyana', prefix: '+592' },
  { code: 'PY', name: 'Paraguay', prefix: '+595' },
  { code: 'PE', name: 'Peru', prefix: '+51' },
  { code: 'SR', name: 'Surinam', prefix: '+597' },
  { code: 'UY', name: 'Uruguay', prefix: '+598' },
  { code: 'VE', name: 'Venezuela', prefix: '+58' },
  // --- Europa ---
  { code: 'ES', name: 'Espana', prefix: '+34' },
];

type Step = 'register' | 'verify';

export default function RegisterPage() {
  const { register, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetFromUrl = searchParams.get('target') || '';

  const [step, setStep] = useState<Step>('register');
  const [email, setEmail] = useState('');
  const [form, setForm] = useState({ nombre_empresa: '', movil: '', pais: 'MX', contacto_nombre: '' });
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [devOtp, setDevOtp] = useState<string | null>(null);

  const updateField = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    // Clear email error when user types
    if (emailError) setEmailError('');
  };

  const handleEmailBlur = () => {
    if (email.trim()) {
      const err = validateCorporateEmail(email.trim());
      setEmailError(err || '');
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate corporate email before submitting
    const corpErr = validateCorporateEmail(email.trim());
    if (corpErr) {
      setEmailError(corpErr);
      return;
    }

    setIsLoading(true);
    try {
      const result = await register({ email: email.trim(), ...form });
      setEmail(result.email);
      setStep('verify');
      startResendCooldown();
      // Try to get OTP from dev endpoint (available when email not configured)
      try {
        const devResp = await authApi.getDevOtp(result.email);
        if (devResp?.otp) setDevOtp(devResp.otp);
      } catch { /* email service active, OTP sent by email */ }
    } catch (err: any) {
      setError(err.message || 'Error al registrarse');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const code = otpCode.join('');
    if (code.length !== 6) return;
    setError('');
    setIsLoading(true);
    try {
      await verifyOtp(email, code);
      navigate(targetFromUrl ? `/app/scans/new?target=${encodeURIComponent(targetFromUrl)}` : '/app');
    } catch (err: any) {
      setError(err.message || 'Codigo invalido');
      setOtpCode(['', '', '', '', '', '']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await resendOtp(email);
      startResendCooldown();
      setError('');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const startResendCooldown = () => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleOtpInput = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otpCode];
    newOtp[index] = value.slice(-1);
    setOtpCode(newOtp);
    if (value && index < 5) {
      const next = document.getElementById(`otp-${index + 1}`);
      next?.focus();
    }
    if (newOtp.every(d => d !== '') && newOtp.join('').length === 6) {
      setTimeout(() => handleVerifyOtp(), 200);
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      const prev = document.getElementById(`otp-${index - 1}`);
      prev?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtpCode(pasted.split(''));
      setTimeout(() => handleVerifyOtp(), 200);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50">
      {/* Left panel - brand */}
      <div className="hidden w-2/5 lg:flex lg:flex-col lg:justify-center lg:px-12 relative overflow-hidden bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900">
        <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIj48ZyBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMSI+PHBhdGggZD0iTTM2IDE4YzMuMzE0IDAgNiAyLjY4NiA2IDZzLTIuNjg2IDYtNiA2LTYtMi42ODYtNi02IDIuNjg2LTYgNi02eiIvPjwvZz48L2c+PC9zdmc+')]" />
        <div className="absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-purple-400/20 blur-[100px]" />

        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm">
              <Shield className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">xFreeScan</h1>
              <p className="text-xs text-purple-300">by XCloud Solutions</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-white leading-tight">
            Diagnostico de seguridad gratuito para su empresa
          </h2>
          <p className="mt-4 text-purple-200">
            Registrese y acceda a escaneos ilimitados. Solo necesita su correo, empresa y movil.
          </p>

          <div className="mt-10 space-y-4">
            {[
              { icon: Shield, text: 'Escaneos ilimitados y gratuitos' },
              { icon: KeyRound, text: 'Acceso seguro con OTP por email' },
              { icon: Globe, text: 'Soporte para sitios web e IPs' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
                  <Icon className="h-4 w-4 text-orange-400" />
                </div>
                <span className="text-purple-100 text-sm">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex w-full flex-col justify-center px-4 py-8 sm:px-6 lg:w-3/5 lg:px-16">
        <div className="mx-auto w-full max-w-md">
          <Link to="/" className="mb-6 flex items-center gap-2 text-gray-500 hover:text-purple-700 transition-colors text-sm">
            <ArrowLeft className="h-4 w-4" /> Volver al inicio
          </Link>

          {step === 'register' ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900">Crear Cuenta</h1>
              <p className="mt-2 text-sm text-gray-500">
                Ya tiene cuenta?{' '}
                <Link to="/login" className="text-purple-600 font-medium hover:text-purple-500">Iniciar sesion</Link>
              </p>

              {error && (
                <div className="mt-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
                </div>
              )}

              <form onSubmit={handleRegister} className="mt-6 space-y-4">
                <div>
                  <label className="label">Nombre de la Empresa *</label>
                  <div className="relative">
                    <Building className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input type="text" value={form.nombre_empresa} onChange={e => updateField('nombre_empresa', e.target.value)}
                      className="input pl-10" placeholder="Mi Empresa S.A." required />
                  </div>
                </div>

                <div>
                  <label className="label">Correo Electronico Corporativo *</label>
                  <div className="relative">
                    <Mail className={`absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 ${emailError ? 'text-red-400' : 'text-gray-400'}`} />
                    <input type="email" value={email}
                      onChange={e => handleEmailChange(e.target.value)}
                      onBlur={handleEmailBlur}
                      className={`input pl-10 ${emailError ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                      placeholder="nombre@suempresa.com" required />
                  </div>
                  {emailError ? (
                    <div className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-red-50 border border-red-200 px-3 py-2">
                      <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-600 font-medium">{emailError}</p>
                    </div>
                  ) : (
                    <div className="mt-1.5 flex items-start gap-1.5">
                      <Info className="h-3 w-3 text-gray-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-gray-400">Solo correos corporativos (ej: nombre@suempresa.com). No se aceptan Gmail, Hotmail, Yahoo, etc.</p>
                    </div>
                  )}
                </div>

                <div className="grid gap-4 grid-cols-5">
                  <div className="col-span-2">
                    <label className="label">Pais *</label>
                    <select value={form.pais} onChange={e => updateField('pais', e.target.value)} className="input">
                      {PAISES.map(p => (
                        <option key={p.code} value={p.code}>{p.prefix} {p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <label className="label">Movil *</label>
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input type="tel" value={form.movil} onChange={e => updateField('movil', e.target.value)}
                        className="input pl-10" placeholder="55 1234 5678" required />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="label">Nombre de Contacto (opcional)</label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input type="text" value={form.contacto_nombre} onChange={e => updateField('contacto_nombre', e.target.value)}
                      className="input pl-10" placeholder="Juan Perez" />
                  </div>
                </div>

                <button type="submit" disabled={isLoading} className="btn-primary w-full mt-2">
                  {isLoading ? (
                    <><div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" /> Enviando codigo...</>
                  ) : (
                    <>Registrarse y Verificar <ArrowRight className="h-5 w-5" /></>
                  )}
                </button>

                <p className="text-center text-xs text-gray-400">
                  Al registrarse acepta los Terminos de Servicio de XCloud Solutions
                </p>
              </form>
            </>
          ) : (
            /* OTP Verification Step */
            <>
              <div className="text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-600 to-purple-700 text-white shadow-xl shadow-purple-500/20">
                  <KeyRound className="h-8 w-8" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Verifique su correo</h1>
                <p className="mt-2 text-sm text-gray-500">
                  Enviamos un codigo de 6 digitos a<br />
                  <span className="font-semibold text-purple-700">{email}</span>
                </p>
              </div>

              {error && (
                <div className="mt-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
                </div>
              )}

              <div className="mt-8 flex justify-center gap-3" onPaste={handleOtpPaste}>
                {otpCode.map((digit, i) => (
                  <input
                    key={i}
                    id={`otp-${i}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleOtpInput(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    className="h-14 w-12 rounded-xl border-2 border-gray-300 bg-white text-center text-2xl font-bold text-purple-900 transition-all focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    autoFocus={i === 0}
                  />
                ))}
              </div>

              <button
                onClick={handleVerifyOtp}
                disabled={isLoading || otpCode.join('').length !== 6}
                className="btn-primary w-full mt-8"
              >
                {isLoading ? (
                  <><div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" /> Verificando...</>
                ) : (
                  <>Verificar y Acceder <ArrowRight className="h-5 w-5" /></>
                )}
              </button>

              <div className="mt-4 text-center">
                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0}
                  className="text-sm text-gray-500 hover:text-purple-600 disabled:opacity-50 inline-flex items-center gap-1"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  {resendCooldown > 0 ? `Reenviar en ${resendCooldown}s` : 'Reenviar codigo'}
                </button>
              </div>

              <button onClick={() => { setStep('register'); setError(''); }} className="mt-4 text-center text-sm text-gray-400 hover:text-gray-600 w-full">
                Cambiar correo electronico
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
